import { useEffect, useRef, useState } from 'react';
import { Volume2 } from 'lucide-react';

/**
 * Vendor intro video that:
 * - Autoplays muted on profile load (browser-allowed)
 * - Unmutes automatically on the first user interaction anywhere on the page
 * - Shows a glowing "Tap for sound" badge until unmuted
 * - Click the video itself to replay from start once it has finished
 *
 * Sound-on-autoplay is blocked by every modern browser. This is the closest legal pattern.
 */
export default function VendorIntroVideo({ src }) {
  const videoRef = useRef(null);
  const [muted, setMuted] = useState(true);
  const [needsTap, setNeedsTap] = useState(true);

  // First interaction anywhere on the page → unmute & play
  useEffect(() => {
    const handler = () => {
      const v = videoRef.current;
      if (!v) return;
      try {
        v.muted = false;
        v.volume = 1;
        const p = v.play();
        if (p && typeof p.then === 'function') p.catch(() => { /* ignore */ });
        setMuted(false);
        setNeedsTap(false);
      } catch { /* ignore */ }
      cleanup();
    };
    const cleanup = () => {
      window.removeEventListener('pointerdown', handler, true);
      window.removeEventListener('keydown', handler, true);
      window.removeEventListener('scroll', handler, true);
      window.removeEventListener('touchstart', handler, true);
    };
    window.addEventListener('pointerdown', handler, true);
    window.addEventListener('keydown', handler, true);
    window.addEventListener('scroll', handler, true);
    window.addEventListener('touchstart', handler, true);
    return cleanup;
  }, []);

  const handleVideoClick = (e) => {
    const v = e.currentTarget;
    // Only unmute on the first click. Do NOT call play()/currentTime here —
    // the <video controls> native UI already handles play/pause/restart.
    // Calling play() here races with the native play button click and was
    // causing the video to look "inactive" after a couple of replays
    // (AbortError swallowed silently by .catch).
    if (v.muted) {
      v.muted = false;
      setMuted(false);
      setNeedsTap(false);
    }
  };

  const handleBadgeClick = (e) => {
    e.stopPropagation();
    const v = videoRef.current;
    if (!v) return;
    v.muted = false;
    v.volume = 1;
    v.play().catch(() => {});
    setMuted(false);
    setNeedsTap(false);
  };

  return (
    <div className="mt-5 relative rounded-2xl overflow-hidden border border-white/10 bg-black max-w-2xl" data-testid="vendor-intro-video">
      <video
        ref={videoRef}
        src={src}
        autoPlay
        muted
        playsInline
        controls
        loop={false}
        preload="auto"
        className="w-full h-auto block"
        onClick={handleVideoClick}
        onEnded={(e) => {
          // After playback finishes, rewind silently so the native "play" /
          // big-overlay button starts from the beginning on the next click.
          // Fixes the "video goes inactive after a few plays" bug.
          try { e.currentTarget.currentTime = 0; } catch { /* ignore */ }
        }}
        onVolumeChange={(e) => setMuted(e.currentTarget.muted)}
      />
      {needsTap && muted && (
        <button
          type="button"
          onClick={handleBadgeClick}
          className="absolute top-3 right-3 z-10 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-yellow-400 text-black text-xs font-bold shadow-[0_0_24px_rgba(250,204,21,0.7)] animate-pulse"
          data-testid="video-unmute-badge"
        >
          <Volume2 size={14} /> Tap for sound
        </button>
      )}
    </div>
  );
}
